import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import TEXT_STYLES, { getCategories, getStylesByCategory, getStyleBySlug } from '../textStyles';
import CopyButton from '../components/CopyButton';
import PlatformPreview from '../components/PlatformPreview';
import { TextStyle, CategoryInfoMap, StyleCategory } from '../types';

// Category display names, descriptions, and icons
const CATEGORY_INFO: CategoryInfoMap = {
  basic: { name: 'Basic', description: 'Bold, italic, monospace fonts', icon: '𝗔𝗮' },
  decorative: { name: 'Decorative', description: 'Cursive, script, gothic fonts', icon: '𝓓' },
  enclosed: { name: 'Enclosed', description: 'Bubble, square letter fonts', icon: 'Ⓔ' },
  aesthetic: { name: 'Aesthetic', description: 'Vaporwave, spaced fonts', icon: 'Ａ' },
  decorator: { name: 'Decorator', description: 'Strikethrough, underline fonts', icon: 'D̲' },
  decorated: { name: 'Decorated', description: 'Hearts, stars, sparkle fonts', icon: '✨' },
  fun: { name: 'Fun', description: 'Upside down, mirror, glitch fonts', icon: '∀' }
};

// Order categories for display
const CATEGORY_ORDER: StyleCategory[] = ['basic', 'decorative', 'enclosed', 'aesthetic', 'decorator', 'decorated', 'fun'];

// Floating symbols for hero background
const FLOATING_SYMBOLS = [
  { char: '𝓐', style: { left: '5%', animationDelay: '0s', animationDuration: '15s' } },
  { char: '𝗕', style: { left: '15%', animationDelay: '2s', animationDuration: '18s' } },
  { char: 'Ⓒ', style: { left: '25%', animationDelay: '4s', animationDuration: '12s' } },
  { char: '𝔇', style: { left: '35%', animationDelay: '1s', animationDuration: '20s' } },
  { char: '𝐄', style: { left: '45%', animationDelay: '3s', animationDuration: '14s' } },
  { char: '✨', style: { left: '55%', animationDelay: '5s', animationDuration: '16s' } },
  { char: '𝘍', style: { left: '65%', animationDelay: '2.5s', animationDuration: '19s' } },
  { char: '🅖', style: { left: '75%', animationDelay: '0.5s', animationDuration: '13s' } },
  { char: '∀', style: { left: '85%', animationDelay: '4.5s', animationDuration: '17s' } },
  { char: '♡', style: { left: '95%', animationDelay: '1.5s', animationDuration: '15s' } },
  { char: '𝕳', style: { left: '10%', animationDelay: '6s', animationDuration: '21s' } },
  { char: '★', style: { left: '30%', animationDelay: '7s', animationDuration: '14s' } },
  { char: '𝙹', style: { left: '50%', animationDelay: '3.5s', animationDuration: '18s' } },
  { char: 'Ｋ', style: { left: '70%', animationDelay: '5.5s', animationDuration: '16s' } },
  { char: '𝑳', style: { left: '90%', animationDelay: '2.2s', animationDuration: '20s' } },
];

// "Find the right generator" routing block: one dedicated page per search intent.
// Anchor text is the query each page should own (GSC showed the homepage ranking
// for these instead of the dedicated pages). Indexable pages only.
const GENERATOR_FINDER: { to: string; label: string; desc: string }[] = [
  { to: '/cursive', label: 'Cursive Font Generator', desc: 'Flowing script letters like 𝓒𝓾𝓻𝓼𝓲𝓿𝓮 for bios, names and captions.' },
  { to: '/bold', label: 'Bold Text Generator', desc: 'One clean, heavy 𝗕𝗼𝗹𝗱 weight for headlines, names and key lines.' },
  { to: '/bold-generator', label: 'Bold Letter Generator', desc: 'Compare 12 bold letter styles side by side: serif, italic, gothic, outlined.' },
  { to: '/italic', label: 'Italic Text Generator', desc: 'Slanted 𝘐𝘵𝘢𝘭𝘪𝘤𝘴 to copy and paste for quotes and emphasis.' },
  { to: '/elegant-fonts', label: 'Elegant Font Generator', desc: 'Elegant cursive and pretty handwriting-style fonts like ℰ𝓁ℯℊ𝒶𝓃𝓉 to copy and paste.' },
  { to: '/times-new-roman-font', label: 'Times New Roman Font Generator', desc: 'Classic serif 𝐓𝐢𝐦𝐞𝐬 bold and 𝑇𝑖𝑚𝑒𝑠 italic letters for bios and posts.' },
  { to: '/aesthetic-fonts', label: 'Aesthetic Font Generator', desc: 'Vaporwave Ａｅｓｔｈｅｔｉｃ and spaced text for soft, minimal profiles.' },
  { to: '/strikethrough', label: 'Strikethrough Text Generator', desc: 'Crossed out S̶t̶r̶i̶k̶e̶ text for jokes, edits and corrections.' },
  { to: '/tiny-text', label: 'Tiny Text Generator', desc: 'Small ᵗⁱⁿʸ superscript letters for subtle captions and usernames.' },
  { to: '/zalgo', label: 'Zalgo Text Generator', desc: 'Glitchy, creepy Z̷a̸l̷g̶o̵ text for Discord and horror posts.' },
  { to: '/old-english', label: 'Old English Font Generator', desc: 'Gothic 𝔒𝔩𝔡 𝔈𝔫𝔤𝔩𝔦𝔰𝔥 blackletter for tattoos, logos and titles.' },
  { to: '/ransom', label: 'Ransom Note Generator', desc: 'Mismatched cut-out 𝐑𝒂𝗻𝘀𝓸𝕞 letters for memes, props and Halloween.' },
  { to: '/fantasy-fonts', label: 'Fantasy Text Generator', desc: 'Medieval gothic, ᚱᚢᚾᛖ runes and royal frames for D&D and RPG names.' },
  { to: '/stylish-name-generator', label: 'Stylish Name Generator', desc: 'Decorated nicknames for PUBG, Free Fire and gaming profiles.' },
  { to: '/discord-fonts', label: 'Discord Font Generator', desc: 'Fonts that work in Discord usernames, nicknames and server names.' },
  { to: '/instagram-fonts', label: 'Instagram Font Generator', desc: 'Curated fonts that display correctly in Instagram bios and captions.' },
  { to: '/twitter-fonts', label: 'Twitter (X) Font Generator', desc: 'Bold, italic and cursive fonts for your X display name, bio and posts.' },
];

// Popular styles for the preview selector
const POPULAR_STYLE_SLUGS = ['bold', 'cursive', 'italic', 'bubble', 'fraktur', 'double-struck', 'sparkles', 'zalgo', 'fullwidth', 'small-caps'];

const getOptionPreview = (text: string): string => Array.from(text).slice(0, 4).join('');

interface StyleCardProps {
  style: TextStyle;
  inputText: string;
  onPreview: (style: TextStyle) => void;
}

function StyleCard({ style, inputText, onPreview }: StyleCardProps): React.ReactElement {
  const transformedText = inputText ? style.transform(inputText) : style.example;

  return (
    <div className="style-card">
      <div className="style-card-header">
        <Link to={`/${style.slug}`} className="style-name">
          {style.name}
        </Link>
        <CopyButton text={transformedText} small />
      </div>
      <Link to={`/${style.slug}`} className="style-preview">
        {transformedText}
      </Link>
      <div className="style-card-footer">
        <button
          className="preview-btn"
          onClick={() => onPreview(style)}
          title="See how it looks on social media"
          suppressHydrationWarning
        >
          📱 Preview
        </button>
        <Link to={`/${style.slug}`} className="style-link">
          Open →
        </Link>
      </div>
    </div>
  );
}

// Preview Modal Component
interface PreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  style: TextStyle | null;
  inputText: string;
}

function PreviewModal({ isOpen, onClose, style, inputText }: PreviewModalProps): React.ReactElement | null {
  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !style) return null;

  const transformedText = inputText ? style.transform(inputText) : style.transform('Hello World');

  return (
    <div className="preview-modal-overlay" onClick={onClose}>
      <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
        <div className="preview-modal-header">
          <h3>
            <span className="preview-modal-example">{style.example.substring(0, 3)}</span>
            {`${style.name} Preview`}
          </h3>
          <button className="preview-modal-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="preview-modal-content">
          <div className="preview-modal-text">
            <span className="preview-modal-label">Your text:</span>
            <span className="preview-modal-styled">{transformedText}</span>
            <CopyButton text={transformedText} />
          </div>
          <PlatformPreview text={transformedText} />
        </div>
      </div>
    </div>
  );
}

function HomePage(): React.ReactElement {
  const [inputText, setInputText] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [previewStyleSlug, setPreviewStyleSlug] = useState<string>('bold');
  const [modalStyle, setModalStyle] = useState<TextStyle | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isSticky, setIsSticky] = useState<boolean>(false);
  const [hasHydrated, setHasHydrated] = useState<boolean>(false);
  const stylesSectionRef = useRef<HTMLElement>(null);
  const stickyRef = useRef<HTMLDivElement>(null);
  const stickyTriggerRef = useRef<HTMLDivElement>(null);

  const categories = getCategories();

  // Get the selected preview style
  const previewStyle = getStyleBySlug(previewStyleSlug);
  const previewText = inputText
    ? (previewStyle ? previewStyle.transform(inputText) : inputText)
    : (previewStyle ? previewStyle.transform('Hello World') : 'Hello World');

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  // Handle sticky behavior
  useEffect(() => {
    const handleScroll = () => {
      if (stickyTriggerRef.current) {
        const triggerTop = stickyTriggerRef.current.getBoundingClientRect().top;
        setIsSticky(triggerTop <= 60); // 60px is header height
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle category selection with scroll
  const handleCategoryClick = useCallback((category: string) => {
    setActiveCategory(category);

    // Scroll to styles section with offset for sticky header
    setTimeout(() => {
      if (stylesSectionRef.current) {
        const headerOffset = isSticky ? 200 : 130;
        const elementPosition = stylesSectionRef.current.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    }, 10);
  }, [isSticky]);

  // Handle opening preview modal
  const handlePreview = useCallback((style: TextStyle) => {
    setModalStyle(style);
    setIsModalOpen(true);
  }, []);

  // Handle closing preview modal
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setModalStyle(null);
  }, []);

  // Get styles to display
  const getDisplayStyles = (): TextStyle[] => {
    if (activeCategory === 'all') {
      return TEXT_STYLES;
    }
    return getStylesByCategory(activeCategory as StyleCategory);
  };

  const displayStyles = getDisplayStyles();

  return (
    <div className="home-page">
      {/* Preview Modal */}
      <PreviewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        style={modalStyle}
        inputText={inputText}
      />

      {/* Hero Section */}
      <section className="hero hero-elevated">
        <div className="hero-floating-symbols" aria-hidden="true" suppressHydrationWarning>
          {FLOATING_SYMBOLS.map((symbol, index) => (
            <span
              key={index}
              className="floating-symbol"
              style={symbol.style}
            >
              {symbol.char}
            </span>
          ))}
        </div>
        <div className="hero-badge" suppressHydrationWarning>✨ Free • No Sign Up • Works Offline</div>
        <h1>Transform Boring Text Into <span className="hero-highlight">Fancy Standout Fonts</span></h1>
        <p className="hero-subtitle">
          {`Free text font generator and transformer with ${TEXT_STYLES.length}+ stylish text styles for Instagram, TikTok, Facebook & more. Type your text, pick a style, preview, copy and paste anywhere!`}
        </p>

        {/* Quick Tool Buttons */}
        <div className="hero-tools">
          <Link to="/bold-generator" className="hero-tool-btn">
            <span className="tool-icon">𝗕</span>
            <span className="tool-name">Bold Letters</span>
          </Link>
          <Link to="/stylish-name-generator" className="hero-tool-btn">
            <span className="tool-icon">𝓝</span>
            <span className="tool-name">Stylish Names</span>
          </Link>
          <Link to="/aesthetic-fonts" className="hero-tool-btn">
            <span className="tool-icon">Ａ</span>
            <span className="tool-name">Aesthetic</span>
          </Link>
          <Link to="/instagram-fonts" className="hero-tool-btn">
            <span className="tool-icon" suppressHydrationWarning>📸</span>
            <span className="tool-name">Instagram</span>
          </Link>
          <Link to="/symbols" className="hero-tool-btn">
            <span className="tool-icon">★</span>
            <span className="tool-name">Symbols & Emojis</span>
          </Link>
        </div>

        {/* Compact Stats */}
        <div className="hero-stats">
          <span className="hero-stat">{`${TEXT_STYLES.length}+ Styles`}</span>
          <span className="hero-stat-divider">•</span>
          <span className="hero-stat">2800+ Symbols</span>
          <span className="hero-stat-divider">•</span>
          <span className="hero-stat">8 Languages</span>
        </div>
      </section>

      {/* Sticky trigger point */}
      <div ref={stickyTriggerRef} className="sticky-trigger"></div>

      {/* Sticky Input + Category Filter Container */}
      <div ref={stickyRef} className={`sticky-input-container ${isSticky ? 'is-sticky' : ''}`}>
        {/* Main Input */}
        <section className="main-input-section">
          <div className="input-container">
            <label htmlFor="main-input" className="input-label">
              Type or paste your text
            </label>
            <textarea
              id="main-input"
              value={inputText}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setInputText(e.target.value)}
              placeholder="Enter your text here to see all styles..."
              rows={isSticky ? 2 : 3}
              className="main-textarea"
            />
            {inputText && (
              <button
                className="clear-btn"
                onClick={() => setInputText('')}
                aria-label="Clear text"
              >
                Clear
              </button>
            )}
          </div>
        </section>

        {/* Category Filter - Desktop Pills */}
        <section className="category-filter desktop-filter">
          <div className="filter-container" suppressHydrationWarning>
            <button
              className={`filter-btn ${activeCategory === 'all' ? 'active' : ''}`}
              onClick={() => handleCategoryClick('all')}
            >
              <span className="filter-icon">✦</span>
              All <span className="filter-count">{`(${TEXT_STYLES.length})`}</span>
            </button>
            {CATEGORY_ORDER.map(cat => {
              if (!categories.includes(cat)) return null;
              const count = getStylesByCategory(cat).length;
              const info = CATEGORY_INFO[cat] || { name: cat };
                return (
                  <button
                    key={cat}
                    className={`filter-btn ${activeCategory === cat ? 'active' : ''}`}
                    onClick={() => handleCategoryClick(cat)}
                  >
                    {info.icon && <span className="filter-icon">{info.icon}</span>}
                    {`${info.name} `}
                    <span className="filter-count">{`(${count})`}</span>
                  </button>
                );
              })}
          </div>
        </section>

        {/* Category Filter - Mobile Dropdown */}
        <section className="category-filter mobile-filter">
          <div className="filter-container" suppressHydrationWarning>
            <label htmlFor="category-select" className="mobile-filter-label">Category:</label>
            <select
              key={hasHydrated ? 'category-controlled' : 'category-hydrating'}
              id="category-select"
              className="category-select"
              {...(hasHydrated ? { value: activeCategory } : { defaultValue: activeCategory })}
              onChange={(e) => handleCategoryClick(e.target.value)}
            >
              <option value="all">{`All Styles (${TEXT_STYLES.length})`}</option>
              {CATEGORY_ORDER.map(cat => {
                if (!categories.includes(cat)) return null;
                const count = getStylesByCategory(cat).length;
                const info = CATEGORY_INFO[cat] || { name: cat };
                return (
                  <option key={cat} value={cat}>
                    {`${info.icon ?? ''} ${info.name} (${count})`}
                  </option>
                );
              })}
            </select>
          </div>
        </section>
      </div>

      {/* Styles Grid */}
      <section className="styles-section" ref={stylesSectionRef}>
        {activeCategory === 'all' ? (
          // Show all styles grouped by category
          CATEGORY_ORDER.map(cat => {
            const categoryStyles = getStylesByCategory(cat);
            if (categoryStyles.length === 0) return null;
            const info = CATEGORY_INFO[cat] || { name: cat };

            return (
              <div key={cat} className="category-group">
                <div className="category-header" suppressHydrationWarning>
                  <h2>
                    {info.icon && <span className="category-icon">{info.icon}</span>}
                    {` ${info.name} Fonts`}
                  </h2>
                  {info.description && <p>{info.description}</p>}
                </div>
                <div className="styles-grid">
                  {categoryStyles.map(style => (
                    <StyleCard
                      key={style.id}
                      style={style}
                      inputText={inputText}
                      onPreview={handlePreview}
                    />
                  ))}
                </div>
              </div>
            );
          })
        ) : (
          // Show filtered styles
          <div className="styles-grid">
            {displayStyles.map(style => (
              <StyleCard
                key={style.id}
                style={style}
                inputText={inputText}
                onPreview={handlePreview}
              />
            ))}
          </div>
        )}
      </section>

      {/* Generator finder: routes each intent to its dedicated page (keyword anchors) */}
      <section className="generator-finder">
        <h2>Find the Right Font Generator</h2>
        <p>Each style has its own dedicated generator with more options, tips and examples:</p>
        <ul className="generator-finder-list">
          {GENERATOR_FINDER.map(item => (
            <li key={item.to}>
              <Link to={item.to}>{item.label}</Link>
              <span>{item.desc}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Platform Previews with Style Selector */}
      <section className="platform-previews-section">
        <h2>Preview Your Style on Social Media</h2>
        <p className="section-subtitle">
          Select a style to see how your text will look on different platforms
        </p>
        <div className="preview-style-selector">
          <label htmlFor="preview-style-select">Style:</label>
          <select
            key={hasHydrated ? 'preview-controlled' : 'preview-hydrating'}
            id="preview-style-select"
            className="preview-style-select"
            {...(hasHydrated ? { value: previewStyleSlug } : { defaultValue: previewStyleSlug })}
            onChange={(e) => setPreviewStyleSlug(e.target.value)}
          >
            <optgroup label="Popular Styles">
              {POPULAR_STYLE_SLUGS.map(slug => {
                const style = getStyleBySlug(slug);
                if (!style) return null;
                return (
                  <option key={slug} value={slug}>
                    {`${getOptionPreview(style.example)} ${style.name}`}
                  </option>
                );
              })}
            </optgroup>
            <optgroup label="All Styles">
              {TEXT_STYLES.filter(s => !POPULAR_STYLE_SLUGS.includes(s.slug)).map(style => (
                <option key={style.slug} value={style.slug}>
                  {`${getOptionPreview(style.example)} ${style.name}`}
                </option>
              ))}
            </optgroup>
          </select>
          <div className="preview-current-text">
            <span className="preview-text-label">Preview:</span>
            <span className="preview-text-styled">{previewText}</span>
            <CopyButton text={previewText} small />
          </div>
        </div>
        <div className="platform-previews-grid">
          <PlatformPreview text={previewText} platform="instagram" />
          <PlatformPreview text={previewText} platform="twitter" />
          <PlatformPreview text={previewText} platform="discord" />
          <PlatformPreview text={previewText} platform="facebook" />
          <PlatformPreview text={previewText} platform="telegram" />
          <PlatformPreview text={previewText} platform="imessage" />
        </div>
      </section>

      {/* Popular Styles Quick Access */}
      <section className="popular-styles">
        <h2>Most Popular Generators</h2>
        <div className="popular-grid">
          <Link to="/bold" className="popular-card">
            <span className="popular-example">𝗕𝗼𝗹𝗱</span>
            <span className="popular-name">Bold Text Generator</span>
          </Link>
          <Link to="/cursive" className="popular-card">
            <span className="popular-example">𝓒𝓾𝓻𝓼𝓲𝓿𝓮</span>
            <span className="popular-name">Cursive Font Generator</span>
          </Link>
          <Link to="/italic" className="popular-card">
            <span className="popular-example">𝘐𝘵𝘢𝘭𝘪𝘤</span>
            <span className="popular-name">Italic Text Generator</span>
          </Link>
          <Link to="/bubble" className="popular-card">
            <span className="popular-example">Ⓑⓤⓑⓑⓛⓔ</span>
            <span className="popular-name">Bubble Letter Generator</span>
          </Link>
          <Link to="/zalgo" className="popular-card">
            <span className="popular-example">Z̷a̸l̷g̶o̵</span>
            <span className="popular-name">Zalgo Text Generator</span>
          </Link>
          <Link to="/upside-down" className="popular-card">
            <span className="popular-example">uʍop ǝpᴉsd∩</span>
            <span className="popular-name">Upside Down Text Generator</span>
          </Link>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-home">
        <h2>How It Works</h2>
        <div className="steps-grid">
          <div className="step">
            <div className="step-number">1</div>
            <h3>Type Your Text</h3>
            <p>Enter any text in the input box above or on any style page.</p>
          </div>
          <div className="step">
            <div className="step-number">2</div>
            <h3>Choose a Style</h3>
            <p>{`Browse ${TEXT_STYLES.length}+ unique styles organized by category.`}</p>
          </div>
          <div className="step">
            <div className="step-number">3</div>
            <h3>Copy & Paste</h3>
            <p>Click copy and paste anywhere—Instagram, TikTok, Twitter, and more.</p>
          </div>
        </div>
      </section>

      {/* Platform-Specific Generators */}
      <section className="worldwide-section">
        <h2>Font Generators by Platform</h2>
        <p>Looking for fonts for a specific platform? We have dedicated generators:</p>
        <div className="languages-grid" suppressHydrationWarning>
          <Link to="/instagram-fonts" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">📸</span>
            <span className="language-name">Instagram Font Generator</span>
            <span className="language-terms">bios, captions, stories</span>
          </Link>
          <Link to="/discord-fonts" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🎮</span>
            <span className="language-name">Discord Font Generator</span>
            <span className="language-terms">usernames, servers, messages</span>
          </Link>
          <Link to="/gaming-fonts" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🕹️</span>
            <span className="language-name">Gaming Fonts</span>
            <span className="language-terms">gamer tags, clan names</span>
          </Link>
        </div>
      </section>

      {/* Used Worldwide Section */}
      <section className="worldwide-section">
        <h2>Used Worldwide</h2>
        <p>People around the world use text generators to make their social media stand out:</p>
        <div className="languages-grid" suppressHydrationWarning>
          <Link to="/es" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇪🇸</span>
            <span className="language-name">Español</span>
            <span className="language-terms">generador de letras bonitas</span>
          </Link>
          <Link to="/pt" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇧🇷</span>
            <span className="language-name">Português</span>
            <span className="language-terms">gerador de fontes de letras</span>
          </Link>
          <Link to="/fr" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇫🇷</span>
            <span className="language-name">Français</span>
            <span className="language-terms">générateur de polices</span>
          </Link>
          <Link to="/id" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇮🇩</span>
            <span className="language-name">Indonesia</span>
            <span className="language-terms">generator font keren</span>
          </Link>
          <Link to="/ph" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇵🇭</span>
            <span className="language-name">Filipino</span>
            <span className="language-terms">font generator</span>
          </Link>
          <Link to="/jp" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇯🇵</span>
            <span className="language-name">日本語</span>
            <span className="language-terms">フォントジェネレーター</span>
          </Link>
          <Link to="/kr" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇰🇷</span>
            <span className="language-name">한국어</span>
            <span className="language-terms">글꼴 생성기</span>
          </Link>
          <Link to="/cn" className="language-item" style={{ textDecoration: 'none' }}>
            <span className="language-flag">🇨🇳</span>
            <span className="language-name">中文</span>
            <span className="language-terms">字体生成器</span>
          </Link>
        </div>
      </section>

      {/* Educational Content: Understanding Unicode */}
      <section className="content-section" style={{ maxWidth: '800px', margin: '0 auto', padding: '2rem 1.5rem' }}>
        <h2>Understanding Unicode: The Engine Behind Fancy Text</h2>
        <p>
          In the early days of computing, text was limited to a simple system called ASCII, which could only
          represent 128 characters — mostly English letters, numbers, and basic punctuation. As the internet
          went global, this was nowhere near enough to represent the world's languages, let alone symbols,
          emojis, and mathematical notations. Enter Unicode: the universal standard for character encoding.
          Unicode's goal is to assign a unique "code point" (a specific number) to every character ever used
          in human history, from ancient Egyptian hieroglyphs to the modern "laughing" emoji.
        </p>
        <p>
          The "fancy text" you see on this site is a creative application of this massive library. Many people
          believe they are seeing new "fonts," but they are actually seeing "glyph substitutions." A font is a
          set of graphical designs applied to a character (like making 'A' look different in Arial vs. Times
          New Roman). Unicode text substitution, on the other hand, replaces the character itself with a
          similar-looking symbol from a different part of the Unicode map. For instance, when you generate
          "Script" text, the tool replaces your standard letters with characters from the "Mathematical
          Alphanumeric Symbols" block or the "Letterlike Symbols" block.
        </p>
        <p>
          This is why Unicode text "just works" when you copy and paste it. If you were to change the font in
          a Word document and paste it into a Facebook comment, the styling would disappear because Facebook
          doesn't support custom font files. But because our generator changes the characters themselves to
          universal symbols that every platform recognizes, the "style" is baked into the text. It is a
          permanent part of the data string, ensuring that whether your friend views your post on an iPhone,
          an Android tablet, or a Windows desktop, they see exactly what you intended.
        </p>
        <p>
          The history of this technology is rooted in the need for scientific precision. The bold, italic, and
          script characters we use for fun today were originally added to Unicode so that mathematicians and
          physicists could write complex formulas without ambiguity. By repurposing these characters, we've
          created a new way to express personality and brand identity in the restrictive, plain-text
          environments of the modern web. From enhancing a gaming "clan" name to making a LinkedIn headline pop,
          Unicode is the invisible thread that makes digital expression possible across every device on earth.
        </p>
      </section>

      {/* FAQ Preview */}
      <section className="faq-preview">
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <details className="faq-item">
            <summary>What is a fancy text generator?</summary>
            <p>
              A fancy text generator converts regular text into special Unicode characters
              that look like different fonts. These characters work everywhere—social media,
              messages, bios—because they're real text, not images or actual fonts.
            </p>
          </details>
          <details className="faq-item">
            <summary>Does fancy text work on Instagram?</summary>
            <p>
              Yes! Fancy text works on Instagram bios, captions, comments, and stories.
              It also works on TikTok, Twitter, Facebook, Discord, WhatsApp, and anywhere
              else that supports Unicode text.
            </p>
          </details>
          <details className="faq-item">
            <summary>Is FancyMyText free to use?</summary>
            <p>
              Completely free! No sign-up, no limits, no watermarks. Use as much as you want.
              You can even install it as an app on your phone for quick access.
            </p>
          </details>
          <details className="faq-item">
            <summary>Why do some characters not convert?</summary>
            <p>
              Unicode doesn't have styled versions of all characters. Numbers, punctuation,
              and special characters may remain unchanged in some styles. Letters A-Z work
              best across all styles.
            </p>
          </details>
          <details className="faq-item">
            <summary>Is it safe to use fancy text on professional platforms like LinkedIn?</summary>
            <p>
              Yes, but moderation is key. Small amounts of bold or italic text can help your profile
              stand out, but overusing decorative symbols can make your content look unprofessional.
              Stick to using it for headings or to highlight your most important skills.
            </p>
          </details>
          <details className="faq-item">
            <summary>Why does my text look different on my friend's phone than on mine?</summary>
            <p>
              While Unicode is a standard, every device has different "system fonts" used to display those
              characters. An emoji looks different on an iPhone than on a Samsung because each company designs
              its own images for those code points. Similarly, fancy letters may appear slightly different
              depending on the device's default font.
            </p>
          </details>
          <details className="faq-item">
            <summary>Does this site store the text I type?</summary>
            <p>
              No. Our fancy text generator operates entirely within your web browser. When you type text
              into the box, the conversion happens locally on your device via JavaScript. We do not see,
              store, or track the content of your messages.
            </p>
          </details>
          <details className="faq-item">
            <summary>Does fancy text work for languages other than English?</summary>
            <p>
              Unicode "fancy fonts" are primarily designed for the Latin alphabet (A-Z). While some symbols
              can mimic characters in other languages, the specific Mathematical Alphanumeric blocks used for
              these styles only cover the standard English alphabet and numbers.
            </p>
          </details>
          <details className="faq-item">
            <summary>What is the difference between fancy "text" and fancy "images"?</summary>
            <p>
              Some sites generate "cool text" by creating a small image of the word. Our site generates actual
              text. The advantage of text is that it is searchable, can be highlighted, is much smaller in file
              size, and works in places where images aren't allowed, like your Instagram bio or a WhatsApp message.
            </p>
          </details>
        </div>
      </section>
    </div>
  );
}

export default HomePage;
